/**
 * Flat retro style SVG icons
 */

interface IconProps {
  className?: string;
  size?: number;
}

/**
 * Magnifying glass icon - for Read/Search
 */
export function MagnifyingGlassIcon({ className = "", size = 20 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
    >
      <circle
        cx="10.5"
        cy="10.5"
        r="6.5"
        stroke="currentColor"
        strokeWidth="2.5"
        fill="none"
      />
      <line
        x1="15.5"
        y1="15.5"
        x2="21"
        y2="21"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * Analog clock icon - for History
 */
export function ClockIcon({ className = "", size = 20 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeWidth="2.5"
        fill="none"
      />
      <line
        x1="12"
        y1="12"
        x2="12"
        y2="7"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <line
        x1="12"
        y1="12"
        x2="16"
        y2="12"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      {/* Hour markers */}
      <circle cx="12" cy="4.5" r="1" fill="currentColor" />
      <circle cx="19.5" cy="12" r="1" fill="currentColor" />
      <circle cx="12" cy="19.5" r="1" fill="currentColor" />
      <circle cx="4.5" cy="12" r="1" fill="currentColor" />
    </svg>
  );
}

/**
 * Tally/counter icon - for Stats/Count
 */
export function TallyIcon({ className = "", size = 20 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
    >
      {/* Four vertical lines */}
      <line x1="5" y1="4" x2="5" y2="20" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="10" y1="4" x2="10" y2="20" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="15" y1="4" x2="15" y2="20" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="20" y1="4" x2="20" y2="20" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      {/* Diagonal strike-through */}
      <line x1="3" y1="18" x2="22" y2="6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Calendar icon - for Date
 */
export function CalendarIcon({ className = "", size = 16 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
    >
      <rect
        x="3"
        y="5"
        width="18"
        height="16"
        rx="2"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
      />
      <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2" />
      <line x1="8" y1="3" x2="8" y2="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="16" y1="3" x2="16" y2="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      {/* Date dots */}
      <circle cx="8" cy="14" r="1.2" fill="currentColor" />
      <circle cx="12" cy="14" r="1.2" fill="currentColor" />
      <circle cx="16" cy="14" r="1.2" fill="currentColor" />
    </svg>
  );
}

/**
 * Stopwatch icon - for Duration
 */
export function StopwatchIcon({ className = "", size = 16 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
    >
      {/* Main circle */}
      <circle
        cx="12"
        cy="13"
        r="8"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
      />
      {/* Top button */}
      <line x1="12" y1="2" x2="12" y2="5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="9" y1="2" x2="15" y2="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      {/* Clock hands */}
      <line x1="12" y1="13" x2="12" y2="9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="12" y1="13" x2="15" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Book/Document icon - for empty history
 */
export function BookIcon({ className = "", size = 48 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
    >
      <path
        d="M4 4C4 3 5 2 6 2H18C19 2 20 3 20 4V20C20 21 19 22 18 22H6C5 22 4 21 4 20V4Z"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
      />
      <line x1="8" y1="7" x2="16" y2="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="8" y1="11" x2="16" y2="11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="8" y1="15" x2="12" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Document icon - for article placeholder
 */
export function DocumentIcon({ className = "", size = 20 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
    >
      <path
        d="M6 2H14L20 8V20C20 21 19 22 18 22H6C5 22 4 21 4 20V4C4 3 5 2 6 2Z"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
      />
      <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <line x1="8" y1="12" x2="16" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="8" y1="16" x2="14" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Speech bubble icon - for fun callouts
 */
export function SpeechBubbleIcon({ className = "", size = 16 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
    >
      <path
        d="M21 12C21 16.4183 16.9706 20 12 20C10.5 20 9.1 19.7 7.8 19.2L3 21L4.5 16.5C3.5 15.1 3 13.6 3 12C3 7.58172 7.02944 4 12 4C16.9706 4 21 7.58172 21 12Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Dots for conversation */}
      <circle cx="8" cy="12" r="1.2" fill="currentColor" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" />
      <circle cx="16" cy="12" r="1.2" fill="currentColor" />
    </svg>
  );
}

/**
 * Quiz/Lightbulb icon - for Quiz feature
 */
export function QuizIcon({ className = "", size = 20 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
    >
      {/* Lightbulb body */}
      <path
        d="M12 2C8.13 2 5 5.13 5 9C5 11.38 6.19 13.47 8 14.74V17C8 17.55 8.45 18 9 18H15C15.55 18 16 17.55 16 17V14.74C17.81 13.47 19 11.38 19 9C19 5.13 15.87 2 12 2Z"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
      />
      {/* Base */}
      <line x1="9" y1="21" x2="15" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      {/* Question mark */}
      <path
        d="M12 7C10.9 7 10 7.9 10 9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="12" cy="12" r="0.8" fill="currentColor" />
    </svg>
  );
}

/**
 * OpenCampus Logo Mark (circular icon only)
 */
export function OpenCampusLogoMark({ className = "", size = 32 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="25 -1 25 26"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <g clipPath="url(#clip0_oc_mark)">
        <path
          d="M37.1316 -0.00563404C34.7681 0.0231703 32.4661 0.735043 30.5152 2.04038C28.5644 3.34573 27.0517 5.18627 26.1677 7.33042C25.2836 9.47458 25.0675 11.8266 25.5467 14.0905C26.0258 16.3545 27.1787 18.4293 28.8605 20.0539C30.5422 21.6785 32.6776 22.7804 34.998 23.2208C37.3183 23.6613 39.7201 23.4207 41.901 22.5293C44.0819 21.6379 45.9446 20.1356 47.2547 18.2113C48.5649 16.2869 49.2639 14.0266 49.264 11.7146C49.2439 8.58681 47.9544 5.59485 45.6792 3.39691C43.404 1.19898 40.3294 -0.0249555 37.1316 -0.00563404Z"
          fill="#00EDBE"
        />
        <path
          d="M41.9263 7.38208C41.8939 6.79602 42.04 6.21371 42.3463 5.70899H32.5663C32.4787 5.70868 32.3918 5.72526 32.3107 5.75779C32.2296 5.79031 32.1559 5.83814 32.0937 5.89854C32.0315 5.95895 31.982 6.03074 31.9482 6.10983C31.9144 6.18892 31.8968 6.27375 31.8965 6.35948V8.40429C31.8968 8.49002 31.9144 8.57485 31.9482 8.65394C31.982 8.73303 32.0315 8.80482 32.0937 8.86523C32.1559 8.92563 32.2296 8.97346 32.3107 9.00599C32.3918 9.03851 32.4787 9.05509 32.5663 9.05478H42.3463C42.04 8.55005 41.8939 7.96775 41.9263 7.38169"
          fill="#141BEB"
        />
        <path
          d="M30.3727 11.7571C30.4046 12.3432 30.2579 12.9253 29.9512 13.4298H39.7311C39.8188 13.4301 39.9056 13.4135 39.9867 13.381C40.0678 13.3485 40.1416 13.3006 40.2038 13.2402C40.266 13.1798 40.3154 13.108 40.3492 13.0289C40.3831 12.9498 40.4007 12.865 40.401 12.7793V10.7345C40.4007 10.6488 40.3831 10.5639 40.3492 10.4848C40.3154 10.4057 40.266 10.3339 40.2038 10.2735C40.1416 10.2131 40.0678 10.1653 39.9867 10.1328C39.9056 10.1003 39.8188 10.0837 39.7311 10.084H29.9512C30.2574 10.5887 30.4036 11.171 30.3711 11.7571"
          fill="#141BEB"
        />
        <path
          d="M41.9263 16.1204C41.8939 15.5343 42.04 14.952 42.3463 14.4473H32.5663C32.4787 14.447 32.3918 14.4635 32.3107 14.4961C32.2296 14.5286 32.1559 14.5764 32.0937 14.6368C32.0315 14.6972 31.982 14.769 31.9482 14.8481C31.9144 14.9272 31.8968 15.012 31.8965 15.0978V17.1426C31.8968 17.2283 31.9144 17.3131 31.9482 17.3922C31.982 17.4713 32.0315 17.5431 32.0937 17.6035C32.1559 17.6639 32.2296 17.7117 32.3107 17.7443C32.3918 17.7768 32.4787 17.7934 32.5663 17.7931H42.3463C42.04 17.2883 41.8939 16.706 41.9263 16.12"
          fill="#141BEB"
        />
      </g>
      <defs>
        <clipPath id="clip0_oc_mark">
          <rect
            width="24.2651"
            height="23.4401"
            fill="white"
            transform="translate(25)"
          />
        </clipPath>
      </defs>
    </svg>
  );
}

/**
 * OpenCampus Full Logo (mark + text)
 */
export function OpenCampusLogo({ className = "", size = 135 }: IconProps) {
  const height = (size / 135) * 24;
  return (
    <svg
      width={size}
      height={height}
      viewBox="25 -1 135 26"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <g clipPath="url(#clip0_610_3941)">
        <path
          d="M37.1316 -0.00563404C34.7681 0.0231703 32.4661 0.735043 30.5152 2.04038C28.5644 3.34573 27.0517 5.18627 26.1677 7.33042C25.2836 9.47458 25.0675 11.8266 25.5467 14.0905C26.0258 16.3545 27.1787 18.4293 28.8605 20.0539C30.5422 21.6785 32.6776 22.7804 34.998 23.2208C37.3183 23.6613 39.7201 23.4207 41.901 22.5293C44.0819 21.6379 45.9446 20.1356 47.2547 18.2113C48.5649 16.2869 49.2639 14.0266 49.264 11.7146C49.2439 8.58681 47.9544 5.59485 45.6792 3.39691C43.404 1.19898 40.3294 -0.0249555 37.1316 -0.00563404Z"
          fill="#00EDBE"
        />
        <path
          d="M41.9263 7.38208C41.8939 6.79602 42.04 6.21371 42.3463 5.70899H32.5663C32.4787 5.70868 32.3918 5.72526 32.3107 5.75779C32.2296 5.79031 32.1559 5.83814 32.0937 5.89854C32.0315 5.95895 31.982 6.03074 31.9482 6.10983C31.9144 6.18892 31.8968 6.27375 31.8965 6.35948V8.40429C31.8968 8.49002 31.9144 8.57485 31.9482 8.65394C31.982 8.73303 32.0315 8.80482 32.0937 8.86523C32.1559 8.92563 32.2296 8.97346 32.3107 9.00599C32.3918 9.03851 32.4787 9.05509 32.5663 9.05478H42.3463C42.04 8.55005 41.8939 7.96775 41.9263 7.38169"
          fill="#141BEB"
        />
        <path
          d="M30.3727 11.7571C30.4046 12.3432 30.2579 12.9253 29.9512 13.4298H39.7311C39.8188 13.4301 39.9056 13.4135 39.9867 13.381C40.0678 13.3485 40.1416 13.3006 40.2038 13.2402C40.266 13.1798 40.3154 13.108 40.3492 13.0289C40.3831 12.9498 40.4007 12.865 40.401 12.7793V10.7345C40.4007 10.6488 40.3831 10.5639 40.3492 10.4848C40.3154 10.4057 40.266 10.3339 40.2038 10.2735C40.1416 10.2131 40.0678 10.1653 39.9867 10.1328C39.9056 10.1003 39.8188 10.0837 39.7311 10.084H29.9512C30.2574 10.5887 30.4036 11.171 30.3711 11.7571"
          fill="#141BEB"
        />
        <path
          d="M41.9263 16.1204C41.8939 15.5343 42.04 14.952 42.3463 14.4473H32.5663C32.4787 14.447 32.3918 14.4635 32.3107 14.4961C32.2296 14.5286 32.1559 14.5764 32.0937 14.6368C32.0315 14.6972 31.982 14.769 31.9482 14.8481C31.9144 14.9272 31.8968 15.012 31.8965 15.0978V17.1426C31.8968 17.2283 31.9144 17.3131 31.9482 17.3922C31.982 17.4713 32.0315 17.5431 32.0937 17.6035C32.1559 17.6639 32.2296 17.7117 32.3107 17.7443C32.3918 17.7768 32.4787 17.7934 32.5663 17.7931H42.3463C42.04 17.2883 41.8939 16.706 41.9263 16.12"
          fill="#141BEB"
        />
      </g>
      <path
        d="M62.84 7.17604C65.7022 7.17604 67.5695 9.09784 67.5695 12.2736C67.5695 14.686 66.1383 17.153 62.8808 17.153C59.9777 17.153 58.165 15.0949 58.165 12.0555C58.165 9.58851 59.7187 7.17604 62.84 7.17604ZM64.9662 12.0964C64.9662 10.27 64.0939 9.39769 62.8808 9.39769C61.477 9.39769 60.6865 10.5017 60.6865 12.1918C60.6865 13.7183 61.5179 14.9177 62.8263 14.9177C64.2847 14.9177 64.9662 13.8001 64.9662 12.0964ZM72.8984 10.1064C74.5067 10.1064 75.8833 11.3059 75.8833 13.5684C75.8833 15.7491 74.4113 16.9622 72.7348 16.9758C71.8353 16.9758 71.0856 16.5533 70.9766 16.3761C70.9766 16.8259 71.0311 19.7154 70.9084 19.7972C70.7858 19.8926 68.9185 19.9744 68.6595 19.8381C68.6186 19.8244 68.7004 10.3654 68.8231 10.0655C68.8367 10.0247 70.8675 10.1337 70.8675 10.1337C70.8675 10.2155 70.8539 10.7879 70.8539 10.8697C70.9493 10.7061 71.6717 10.1064 72.8984 10.1064ZM73.5799 13.5956C73.5799 12.6688 73.021 12.1782 72.285 12.1782C71.3582 12.1782 70.9221 12.7642 70.9221 13.582C70.9221 14.4816 71.549 15.0268 72.2305 15.0268C73.0892 15.0268 73.5799 14.4407 73.5799 13.5956ZM78.751 14.1136C78.751 14.836 79.2689 15.2857 80.2093 15.2857C80.9453 15.2857 81.4496 15.0268 81.804 14.8087C81.9403 14.945 82.4991 16.0763 82.5128 16.2807C82.1448 16.6214 81.2043 17.0576 80.1003 17.0576C77.6333 17.0576 76.6384 15.4084 76.6384 13.6638C76.6384 11.8238 77.7832 10.1064 79.9231 10.1064C81.7359 10.1064 82.8399 11.4694 82.8399 12.9959C82.8399 13.4185 82.799 13.7728 82.7172 14.0454C82.0357 14.1408 79.5687 14.1408 78.751 14.1136ZM78.7373 12.8596L80.8772 12.8188C80.9045 12.1509 80.5637 11.633 79.9095 11.633C79.2007 11.633 78.7373 12.1645 78.7373 12.8596ZM83.8472 10.3381C83.9699 10.161 85.878 10.0792 86.0007 10.2018C85.9871 10.4881 85.9734 10.9378 85.9598 11.0196C86.2597 10.5698 86.9275 10.0655 88.0315 10.0655C89.5172 10.0655 90.444 11.2513 90.444 13.0096C90.444 13.9228 90.3895 16.2262 90.335 16.894C90.3213 17.0303 88.2769 17.0031 88.236 16.9758C88.1815 16.9486 88.2496 13.7319 88.2496 13.2413C88.2496 12.5053 87.9225 11.9873 87.2137 11.9873C86.4641 11.9873 86.0143 12.5734 86.0143 13.105C86.0143 13.5684 86.0007 16.8395 85.9598 16.894C85.9189 16.9486 83.929 16.9213 83.8881 16.8395C83.8608 16.785 83.8063 13.2276 83.8472 10.3381ZM96.732 12.1509C96.732 14.0999 97.9314 14.8768 99.0899 14.8768C100.194 14.8768 100.944 14.2908 101.243 14.0727C101.557 14.2908 102.157 15.3948 102.375 15.8582C101.789 16.526 100.412 17.1803 98.8446 17.1803C95.6552 17.1803 94.1696 14.9041 94.1696 12.0555C94.1696 9.31591 96.105 7.2033 99.199 7.2033C100.766 7.2033 101.898 7.69397 102.32 8.10286C102.32 8.37546 101.748 9.84747 101.407 10.1337C100.916 9.84747 100.085 9.47947 99.2262 9.47947C97.8224 9.47947 96.732 10.5971 96.732 12.1509ZM103.035 11.5512C103.048 11.3195 103.948 10.0383 105.774 10.0383C107.464 10.0383 108.773 11.0333 108.773 13.0232C108.773 14.4816 108.705 16.7441 108.664 16.8259C108.623 16.9077 106.905 16.8259 106.905 16.8259L106.783 16.2398C106.687 16.3352 106.115 16.9895 105.038 16.9895C103.552 16.9895 102.926 16.1171 102.926 14.9314C102.926 13.7047 104.002 12.9278 105.365 12.9278C105.992 12.9278 106.374 13.105 106.633 13.2958C106.646 12.7097 106.674 11.9056 105.447 11.9056C104.602 11.9056 104.207 12.5053 104.084 12.5189C103.961 12.5189 103.021 11.7011 103.035 11.5512ZM104.752 14.8905C104.752 15.3403 105.134 15.6537 105.638 15.6537C106.238 15.6537 106.537 15.2994 106.537 14.8905C106.537 14.4134 106.142 14.1136 105.638 14.1136C105.106 14.1136 104.752 14.4543 104.752 14.8905ZM110.108 10.2564C110.231 10.0519 112.139 10.1746 112.262 10.2973C112.248 10.5835 112.235 10.9515 112.221 11.0333C112.521 10.5835 113.039 10.1064 114.143 10.1064C114.906 10.1064 115.519 10.4881 115.887 11.0878C116.351 10.27 117.387 10.1064 117.945 10.1064C119.663 10.1064 120.426 11.2377 120.426 12.9823C120.426 13.9091 120.372 16.2671 120.331 16.9077C120.317 17.0303 118.245 17.0303 118.204 16.9895C118.164 16.9622 118.232 13.841 118.232 13.2004C118.232 12.5325 118.055 12.0282 117.332 12.0282C116.583 12.0282 116.364 12.7642 116.364 13.1868C116.364 14.1681 116.324 16.1853 116.283 16.8668C116.269 16.9895 114.211 16.9077 114.184 16.8804C114.129 16.8532 114.197 13.6638 114.197 13.2004C114.197 12.5325 114.007 12.0282 113.298 12.0282C112.589 12.0282 112.289 12.6279 112.289 13.0641C112.289 13.4594 112.262 16.9077 112.221 16.9622C112.18 17.0167 110.19 16.9895 110.149 16.9077C110.122 16.8532 110.068 13.1459 110.108 10.2564ZM125.993 10.1064C127.601 10.1064 128.978 11.3059 128.978 13.5684C128.978 15.7491 127.506 16.9622 125.83 16.9758C124.93 16.9758 124.18 16.5533 124.071 16.3761C124.071 16.8259 124.126 19.7154 124.003 19.7972C123.88 19.8926 122.013 19.9744 121.754 19.8381C121.713 19.8244 121.795 10.3654 121.918 10.0655C121.931 10.0247 123.962 10.1337 123.962 10.1337C123.962 10.2155 123.949 10.7879 123.949 10.8697C124.044 10.7061 124.766 10.1064 125.993 10.1064ZM126.675 13.5956C126.675 12.6688 126.116 12.1782 125.38 12.1782C124.453 12.1782 124.017 12.7642 124.017 13.582C124.017 14.4816 124.644 15.0268 125.325 15.0268C126.184 15.0268 126.675 14.4407 126.675 13.5956ZM136.439 16.8668C136.316 17.044 134.544 16.9895 134.408 16.8668C134.422 16.5806 134.408 16.1853 134.422 16.0899C134.122 16.526 133.427 17.0985 132.405 17.0985C130.755 17.0985 129.965 16.0899 129.965 14.1817C129.965 13.2004 130.101 10.8697 130.142 10.2291C130.169 10.0928 132.214 10.1201 132.255 10.161C132.282 10.3109 132.146 13.4185 132.146 13.95C132.146 14.7133 132.473 15.1767 133.181 15.1767C133.931 15.1767 134.326 14.6042 134.34 14.0045C134.34 13.6093 134.394 10.2564 134.449 10.2018C134.49 10.1473 136.466 10.1746 136.521 10.2564C136.534 10.3109 136.493 13.9637 136.439 16.8668ZM140.394 17.1257C139.031 17.1257 137.791 16.5806 137.6 16.3761C137.491 16.2398 138.118 14.7542 138.227 14.8087C138.445 14.9177 139.29 15.5311 140.162 15.5311C140.707 15.5311 140.98 15.3403 140.98 15.0404C140.98 14.7405 140.762 14.577 139.876 14.4134C138.349 14.1545 137.641 13.5139 137.641 12.369C137.641 10.9106 138.636 10.0655 140.312 10.0655C141.566 10.0655 142.67 10.4881 142.847 10.6653C142.97 10.7879 142.343 12.219 142.248 12.2327C142.179 12.2327 141.307 11.7011 140.517 11.7011C139.985 11.7011 139.753 11.9601 139.753 12.2327C139.753 12.5325 139.985 12.6688 140.776 12.7779C142.397 13.0096 143.12 13.5684 143.12 14.836C143.12 16.0354 142.302 17.1257 140.394 17.1257Z"
        fill="currentColor"
      />
      <path
        d="M147.272 7.43501C147.709 7.27145 149.617 7.32597 149.699 7.40775C149.794 7.9802 149.78 16.0217 149.685 16.894C149.603 17.1394 147.272 17.044 147.259 16.9486C147.204 15.9809 147.163 8.74346 147.272 7.43501ZM151.544 16.8532C151.476 16.0354 151.53 8.41635 151.639 7.40775C151.953 7.32597 153.547 7.29871 154.624 7.29871C157.813 7.29871 159.762 9.37043 159.762 12.1373C159.762 14.9859 158.236 17.044 154.501 17.044C153.343 17.044 151.844 16.9486 151.544 16.8532ZM157.241 12.2599C157.241 10.638 156.137 9.56125 154.651 9.56125C154.338 9.56125 154.106 9.57488 153.929 9.58851C153.943 11.1559 153.902 13.2004 153.874 14.7678C154.12 14.8087 154.406 14.8223 154.747 14.8223C155.81 14.8223 157.241 14.4816 157.241 12.2599Z"
        fill="#00EDBE"
      />
      <defs>
        <clipPath id="clip0_610_3941">
          <rect
            width="24.2651"
            height="23.4401"
            fill="white"
            transform="translate(25)"
          />
        </clipPath>
      </defs>
    </svg>
  );
}
